// ** Core Imports
import { React, useEffect, useState } from "react"
import AsyncSelect from "react-select/async"
import { Controller, useForm } from "react-hook-form"
import { useSelector } from "react-redux"
// ** Icons
import { CreditCard, Truck } from "react-feather"
// ** Third Party Components
import {
  Col,
  Form,
  Button,
  Label,
  Row,
  InputGroup,
  InputGroupText,
  Input,
  FormFeedback,
  Spinner,
  Table,
  Badge,
  FormText
} from "reactstrap"
import Swal from "sweetalert2"
import Flatpickr from "react-flatpickr"
import withReactContent from "sweetalert2-react-content"
// ** Utils
import {
  getISODateUTC,
  getCurrencyFromCountry,
  provideOptionsToSelect,
  checkFieldValidity
} from "@lib/util"
import {
  getAllFromController,
  getPersonInteractions,
  postOfferSaleToConfigurator,
  getLoggedInUser
} from "@lib/api"
import SelectShowroomListbox from "../../components/SelectShowroomListbox"
import { UNKNOWN_PARTNER } from "@lib/constants"
const NewOfferSaleForm = (props) => {
  const store = useSelector((state) => state.persons)
  const MySwal = withReactContent(Swal)
  const today = getISODateUTC().split("Z")[0]
  const [user, setUser] = useState({})

  // constants
  const defaultValues = {
    date: today,
    weight: "",
    height: "",
    currencyId: 0,
    currencyLabel: "",
    deliveryAddressId: store.selectedPerson.addresses[0].id,
    billingAddressId: store.selectedPerson.addresses[0].id
  }
  // Hooks and States
  const {
    control,
    setError,
    setValue,
    watch,
    handleSubmit,
    formState: { isDirty, errors, isSubmitting, isSubmitSuccessful }
  } = useForm({ defaultValues })

  const watchDeliveryAddressId = parseInt(watch("deliveryAddressId"))

  useEffect(() => {
    // get user info
    getLoggedInUser().then((user) => setUser(user))
  }, [])
  useEffect(() => {
    // on change of selected delivery address,
    // get the country of the address via html data attribute,
    // then get its currency via util function
    const fetchData = async (countryId) => {
      try {
        const currency = await getCurrencyFromCountry(countryId)
        setValue("currencyId", currency.id)
        setValue("currencyLabel", currency.iso3)
      } catch (err) {
        console.error(err)
      }
    }
    if (watchDeliveryAddressId) {
      const deliveryAddressCountryId = parseInt(
        document.querySelector('input[name="deliveryAddressId"]:checked').dataset.country
      )
      fetchData(deliveryAddressCountryId)
    }
  }, [watchDeliveryAddressId])

  // Get data from Config API and format it for select component
  const formatConfigDataForSelect = async (controllerName) => {
    const res = await getAllFromController("OrdersAPI/formData", "config")
    const formatedData = []
    const idFieldName = controllerName === "sleepPositions" ? "sleepPositionID" : "recoveryFeeID"
    res[controllerName].forEach((item) =>
      formatedData.push({
        label: item.name,
        value: item[idFieldName]
      })
    )
    return formatedData
  }

  // Submit form functions
  const onSubmit = async (data) => {
    // Form validation
    let error = false
    const optionalFields = ["date", "sleepingPosition", "partner", "showroomId"]
    for (const key in data) {
      if (optionalFields.includes(key)) continue
      // special validation for height and weight
      if (["weight", "height"].includes(key)) {
        const value = parseInt(data[key])
        if (value < 1 || value > 999) {
          setError(key, {
            type: "manual",
            message: `${key} must be between 1-999`
          })
          error = true
        }
      } else {
        if (!checkFieldValidity(data[key])) {
          setError(key, {
            type: "manual",
            message: `'${key}' is required`
          })
          error = true
        }
      }
    }

    if (!user.collaboratorId || !user.internalCompanyId || !user.showroomId) {
      MySwal.fire({
        title: "Error",
        allowOutsideClick: false,
        html: `
        <div>
          <p>Your account is missing the following information :</p>
          ${!user.collaboratorId ? "<p>collaboratorId</p>" : ""}
          ${!user.internalCompanyId ? "<p>internalCompanyId</p>" : ""}
          ${!user.showroomId ? "<p>showroomId</p>" : ""}
        </div>`,
        icon: "error"
      })
      return
    }
    if (!error) {
      try {
        // get the first [0] interaction between a person and the loggedIn user's company
        const interactions = await getPersonInteractions(props.personId, user.internalCompanyId)
        const firstInteractionId = interactions[0].id
        // send all form + event data to Configurator API
        const dataForConfigurator = {
          ...(data.sleepingPosition?.value && { sleepingPosition: data.sleepingPosition.value }),
          ...(data.weight?.length > 0 && { weight: data.weight }),
          ...(data.height?.length > 0 && { height: data.height }),
          ...(data.partner?.value && { partnerId: data.partner.value }),
          eventReasonId: props.eventReason.id,
          currencyID: data.currencyId,
          billingAddressId: parseInt(data.billingAddressId),
          deliveryAddressId: parseInt(data.deliveryAddressId),
          collaboratorId: user.collaboratorId,
          showroomId: data.showroomId || user.showroomId,
          description: data.description,
          interactionId: firstInteractionId,
          // weird formatting with flatpickr
          on: data.date.length === 1 ? getISODateUTC(new Date(data.date)) : today
        }
        const postToConfigResponse = await postOfferSaleToConfigurator(dataForConfigurator)
        if (postToConfigResponse.orderID) {
          window.location.href = `/configurator/orders/details/${postToConfigResponse.orderID}`
        }
      } catch (err) {
        console.error(err)
        MySwal.fire({
          title: "Error",
          text: err.message,
          icon: "error"
        })
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center mb-2">
        <h1 className="mb-1">Add New {props.eventReason.label}</h1>
        <p>Create a new {props.eventReason.label} in the Configurator.</p>
      </div>
      <h5>Optional information</h5>
      <Row className="mb-1">
        <Col>
          <Label for="sleepingPosition">Sleeping position</Label>
          <Controller
            name="sleepingPosition"
            id="sleepingPosition"
            control={control}
            render={({ field }) => (
              <>
                <AsyncSelect
                  {...field}
                  isClearable={false}
                  isSearchable={false}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={() => formatConfigDataForSelect("sleepPositions")}
                />
                {errors.sleepingPosition && (
                  <small style={{ color: "#ea5455" }}>{errors.sleepingPosition.message}</small>
                )}
              </>
            )}
          />
        </Col>
        <Col>
          <Label for="partner">Sent by (partner)</Label>
          <Controller
            name="partner"
            id="partner"
            control={control}
            render={({ field }) => (
              <>
                <AsyncSelect
                  {...field}
                  isClearable={true}
                  isSearchable={true}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={(inputSearchValue) =>
                    provideOptionsToSelect({
                      name: `Partners?filters[entityId]=gt_${UNKNOWN_PARTNER.id}&search=${inputSearchValue}`,
                      apiEndpoint: "crm"
                    })
                  }
                />
              </>
            )}
          />
        </Col>
      </Row>
      <Row className="mb-1">
        <Col>
          <Row>
            <Label for="weight">Weight</Label>
            <Col>
              <InputGroup className="input-group">
                <InputGroupText>kg</InputGroupText>
                <Controller
                  name="weight"
                  id="weight"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        type="number"
                        placeholder="80"
                        invalid={errors.weight && true}
                      />
                      {errors.weight && <FormFeedback>{errors.weight.message}</FormFeedback>}
                    </>
                  )}
                />
              </InputGroup>
            </Col>
          </Row>
        </Col>
        <Col>
          <Row>
            <Label for="height">Height</Label>
            <Col>
              <InputGroup className="input-group">
                <InputGroupText>cm</InputGroupText>
                <Controller
                  name="height"
                  id="height"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        type="number"
                        placeholder="170"
                        invalid={errors.height && true}
                      />
                      {errors.height && <FormFeedback>{errors.height.message}</FormFeedback>}
                    </>
                  )}
                />
              </InputGroup>
            </Col>
          </Row>
        </Col>
      </Row>

      <hr className="my-3" />
      <h5>Required information</h5>

      <Row className="mb-1">
        <Col>
          <Label for="eventDate">Date *</Label>
          <Controller
            name="date"
            id="date"
            control={control}
            render={({ field }) => (
              <Flatpickr
                {...field}
                className="form-control"
                placeholder={today}
                options={{
                  dateFormat: "Y-m-d H:i",
                  locale: {
                    firstDayOfWeek: 1
                  },
                  maxDate: today,
                  enableTime: true,
                  time_24hr: true
                }}
              />
            )}
          />
          <FormText className="text-muted">Automatically set to today</FormText>
        </Col>
        <Col>
          <Label for="currency">Currency *</Label>
          <Controller
            id="currencyId"
            name="currencyId"
            control={control}
            render={({ field }) => <Input {...field} type="hidden" />}
          />
          <Controller
            id="currencyLabel"
            name="currencyLabel"
            control={control}
            render={({ field }) => <Input {...field} type="text" disabled />}
          />
          <FormText className="text-muted">Automatically set based on delivery address</FormText>
        </Col>
      </Row>
      <Row className="mb-1">
        <Col>
          <Label for="description">{props.eventReason.label} summary *</Label>
          <Controller
            id="description"
            name="description"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  type="textarea"
                  rows="4"
                  placeholder="A quick description"
                  invalid={errors.description && true}
                />
                {errors.description && <FormFeedback>{errors.description.message}</FormFeedback>}
              </>
            )}
          />
        </Col>
      </Row>
      <Row>
        <Label>Select the addresses *</Label>
        <Table responsive borderless striped className="text-nowrap text-center" size="sm">
          <thead>
            <tr>
              <th></th>
              <th>
                <Truck size={16} />
                <br />
                Delivery Address
              </th>
              <th>
                <CreditCard size={16} />
                <br />
                Billing Address
              </th>
            </tr>
          </thead>
          <tbody>
            {store.selectedPerson.addresses.map((address, index) => {
              return (
                <tr key={index}>
                  <td className="text-start">
                    <div>
                      <span>{address.street}</span>
                      {address.additional && <span>, {address.additional}</span>}
                      <br />
                      <span>
                        {address.postCode} {address.city.name}
                      </span>
                      <br />
                      <span>
                        {address.city.state.nameEN} {address.city.state.country.iso2}
                      </span>
                    </div>
                    <Badge color="light-primary" pill>
                      {address.type.name}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex form-check justify-content-center">
                      <Controller
                        name="deliveryAddressId"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="cursor-pointer"
                            id={`deliveryAddressId-${index}`}
                            type="radio"
                            data-country={address.city.state.country.id}
                            value={address.id}
                            defaultChecked={index === 0}
                          />
                        )}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex form-check justify-content-center">
                      <Controller
                        name="billingAddressId"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            className="cursor-pointer"
                            id={`billingAddressId-${index}`}
                            type="radio"
                            value={address.id}
                            defaultChecked={index === 0}
                          />
                        )}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
            <tr>
              <td></td>
              <td>
                {errors.deliveryAddressId && (
                  <div className="text-center">
                    <small style={{ color: "#ea5455" }}>{errors.deliveryAddressId.message}</small>
                  </div>
                )}
              </td>
              <td>
                {errors.billingAddressId && (
                  <div className="text-center">
                    <small style={{ color: "#ea5455" }}>{errors.billingAddressId.message}</small>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </Row>

      <hr className="my-3" />

      <Row className="d-flex">
        <Col>
          <SelectShowroomListbox user={user} control={control} />
        </Col>
        <Col className="align-self-center text-end">
          {isSubmitting || isSubmitSuccessful ? (
            <Button color="light" disabled>
              {isSubmitting && <Spinner color="dark" size="sm" />}
              <span className="ms-50">{isSubmitting ? "Loading..." : "Submitted"}</span>
            </Button>
          ) : (
            <Button color="success" type="submit" disabled={!isDirty}>
              Add {props.eventReason.label}
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  )
}

export default NewOfferSaleForm
